import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileInput, Database, Users, Package, FileText, BarChart3, DollarSign,
  CheckSquare, Copy, ListChecks, Zap, ArrowLeft, CheckCircle
} from 'lucide-react';

// The pipeline, in the exact order the backend runs it (services/ediService
// ingestion → services/validationService 6 dimensions → adjudication).
const PHASES = [
  {
    phase: 'Phase 1 · Ingestion',
    blurb: 'The uploaded file is parsed and turned into claims and line items.',
    steps: [
      {
        icon: FileInput, title: 'Parse & validate EDI structure',
        desc: 'The X12 844 is parsed and structurally checked — interchange/group/transaction envelopes (ISA/GS/ST), the BEG beginning segment (transaction purpose code), IT1 line items, PID descriptions, and the CTT control total. A 997 functional acknowledgement is generated. Malformed files are rejected before any claim is created. As an alternative to X12, you can upload a named-column CSV (claim_number, customer_edi_id, contract_alias, product_ndc, quantity, sales_price, …) through the same “Upload EDI 844 File” action — it links customers by EDI id, the contract by number/alias, and products by NDC, then joins the same pipeline from the next step on.',
        codes: []
      },
      {
        icon: Database, title: 'Create records',
        desc: 'A hierarchy is created: InboundFile → ChargebackBatch → ChargebackClaim → ChargebackLine. Purpose codes are read here too — a resubmission (04/05/6R) supersedes the prior claim; a cancellation (01) voids it.',
        codes: []
      },
      {
        icon: Users, title: 'Resolve customer',
        desc: 'The N1 buying party is matched to a Customer — first by identifier (NPI, tax ID, or EDI sender/receiver ID), then by name. Unmatched or inactive customers are flagged.',
        codes: ['CUSTNF', 'CUSTIN', 'CUSTNC']
      },
      {
        icon: Package, title: 'Resolve product',
        desc: "Each line's NDC / product code is matched to a Product in the catalog. The product may be resolved later during validation if it isn't in the catalog at import time.",
        codes: ['PRODNF', 'PRODIN', 'PRODNDC']
      }
    ]
  },
  {
    phase: 'Phase 2 · Line validation',
    blurb: 'Every line item runs through six validation dimensions, in this order. The first dimension that fails determines the line’s status code.',
    steps: [
      {
        icon: FileText, title: '1. Contract validation',
        desc: 'Confirms an active, effective contract exists for this customer and product on the sale date — checking the customer is associated with the contract, the product is priced in it, and the sale date falls inside the contract’s effective window.',
        codes: ['CNTRNF', 'CNTRIN', 'CNTRXP', 'CNTRNC', 'CNTRDT', 'CNTRCA', 'CNTRNP']
      },
      {
        icon: Package, title: '2. Product validation',
        desc: 'Verifies the product is found, active, chargeback-eligible, and its NDC is well-formed (and controlled-substance / stock rules where applicable).',
        codes: ['PRODNF', 'PRODIN', 'PRODNDC', 'PRODCS', 'PRODOOS']
      },
      {
        icon: BarChart3, title: '3. Sales data validation',
        desc: 'Looks for a matching wholesaler sales record that substantiates the claim, and compares its price and volume against what was claimed. Missing sales data is surfaced as a warning rather than a hard block.',
        codes: ['SALDNF', 'SALDPM', 'SALDVV']
      },
      {
        icon: DollarSign, title: '4. Pricing & tolerance validation',
        desc: 'Resolves the ContractPrice effective on the sale date (rows where effectiveFrom ≤ salesDate ≤ effectiveTo, ordered by contract priority), computes variance = claimed price − contract price, and checks it against the contract’s Tolerance record (percentage band or threshold amount). Never a hardcoded percentage.',
        codes: ['PRICNF', 'PRICVT', 'PRICNG', 'PRICUH']
      },
      {
        icon: CheckSquare, title: '5. Business rules',
        desc: 'Applies required-field and sanity rules: quantity must be a whole number greater than zero, the sale date can’t be in the future or more than a year old, and the amount math must reconcile.',
        codes: ['BUSQTY', 'BUSDAT', 'BUSAMT', 'BUSCOM']
      },
      {
        icon: Copy, title: '6. Duplicate / double-dip check',
        desc: 'Fingerprints the line (customer + invoice + NDC + sale date + quantity) and flags a later occurrence as a duplicate, so the same physical sale can’t be claimed twice.',
        codes: ['DATADUP', 'DATAQTY', 'DATAMIS']
      }
    ]
  },
  {
    phase: 'Phase 3 · Outcome',
    blurb: 'Results roll up from lines to the claim, ready for adjudication.',
    steps: [
      {
        icon: ListChecks, title: 'Status roll-up',
        desc: 'Each line receives a LineItemValidationStatus code. If every line passes, the claim becomes Validated; if any line fails, the claim becomes In Error and the reason is recorded — shown inline under the status on the Chargebacks list and, on the claim, in a “Why this claim is in error” panel with each line’s code. Running validation is what moves a claim off Imported; it is on-demand, never automatic.',
        codes: ['VALIDATED', 'ERROR']
      },
      {
        icon: Zap, title: 'Adjudication',
        desc: 'Auto-Adjudicate (or manual review) approves clean claims — optionally staging a draft credit memo — and routes failures to the In Error queue with their reason codes. Nothing is auto-rejected or posted to the GL.',
        codes: []
      }
    ]
  }
];

const CodeChip = ({ code }) => (
  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs font-medium text-gray-600">{code}</span>
);

const Edi844Validation = () => {
  const navigate = useNavigate();
  let stepNo = 0;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <button onClick={() => navigate('/app/guide')} title="Back to User Guide"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> User Guide
      </button>

      {/* Hero */}
      <div className="mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-indigo-800 p-8 text-white shadow-lg">
        <h1 className="text-3xl font-bold">EDI 844 Validation Pipeline</h1>
        <p className="mt-2 max-w-2xl text-white/85">
          Every step an uploaded EDI 844 chargeback file goes through — from parsing to adjudication — in the exact order the system runs them, with the status codes each step can raise.
        </p>
      </div>

      <div className="space-y-10">
        {PHASES.map((p) => (
          <section key={p.phase}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{p.phase}</h2>
              <p className="text-sm text-gray-500">{p.blurb}</p>
            </div>

            <ol className="relative space-y-4 border-l-2 border-gray-100 pl-6">
              {p.steps.map((s) => {
                stepNo += 1;
                const Icon = s.icon;
                return (
                  <li key={s.title} className="relative">
                    {/* node on the timeline */}
                    <span className="absolute -left-[35px] flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white ring-4 ring-white">
                      {stepNo}
                    </span>
                    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition hover:shadow-md">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-primary-50 p-2 text-primary-700"><Icon className="h-5 w-5" /></div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900">{s.title}</h3>
                          <p className="mt-1 text-sm leading-relaxed text-gray-600">{s.desc}</p>
                          {s.codes.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                              <span className="text-xs text-gray-400">Status codes:</span>
                              {s.codes.map((c) => <CodeChip key={c} code={c} />)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}

        {/* End state */}
        <div className="flex items-center gap-3 rounded-xl border border-green-100 bg-green-50 p-5">
          <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600" />
          <div className="text-sm text-green-900">
            <span className="font-semibold">Result:</span> a clean claim ends up <span className="font-medium">Validated → Approved</span> with a draft credit memo; anything that fails a step lands in the <span className="font-medium">In Error</span> queue with the specific code(s) above, ready for a person to review or reject.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edi844Validation;
